/* eslint-disable max-lines-per-function */
import React, { useMemo } from 'react';
import type { Nullable } from 'corgi-types';

import { adaptField, adaptFieldWithBlur } from 'common/components/DynamicForm/utils/field-adapter';
import PasswordField from 'common/components/Form/PasswordField';
import TitleField from 'common/components/Form/TitleField';
import UserTypeField from 'common/components/Form/UserTypeField';
import type { Slices } from 'common/components/HomeClubDrawer/types';
import StatefulInput from 'common/components/Input/StatefulInput';
import { InputWithReplace } from 'common/components/Input/StatefulInput/InputVariants';
import Separator from 'common/components/Separator';
import { HeadlineH7 } from 'common/components/Typography';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { HTMLAutoCompleteAttribute } from 'common/enums/form';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { FormData } from 'common/types/form';
import { useIsB2BEnabled } from 'common/utils/b2b';
import { FieldName } from '../enums';
import { ConsentCheckbox } from '../Form/ConsentCheckbox';
import { HomeClubConsentCheckbox } from '../Form/HomeClubConsentCheckbox';
import { NewsletterConsentCheckbox } from '../Form/NewsletterConsentCheckbox';
import { removeWhiteSpaces } from '../Form/utils';
import { useHomeClubDrawer } from './home-club-drawer';
import type { ValidationRules } from './validation-rules';

interface FormGroupsArgs {
  showHomeClubConsent: boolean;
  isBusinessFormActive: boolean;
  isThirdPartySignUpFlow: boolean;
  homeClubSlices: Nullable<Slices>;
  initialFormData?: FormData<FieldName>;
  validationRules: Nullable<ValidationRules>;
}

export const useFormGroups = ({
  homeClubSlices,
  validationRules,
  initialFormData,
  showHomeClubConsent,
  isBusinessFormActive,
  isThirdPartySignUpFlow,
}: FormGroupsArgs) => {
  const { t } = useLocalization();
  const showUserTypeField = useIsB2BEnabled();
  const hasHomeClubNudge = useFeatureFlagIsOn(GrowthbookFlag.HomeClubNudgeInRegistration);

  const onHomeClubLinkClick = useHomeClubDrawer({
    homeClubSlices,
  });

  const formGroups = useMemo(
    // eslint-disable-next-line complexity
    () => [
      ...(showUserTypeField
        ? [
            {
              Component: adaptField(UserTypeField),
              initialValue: initialFormData?.get(FieldName.UserType),
              name: FieldName.UserType,
              validation: {
                required: false,
                rules: null,
              },
            },
          ]
        : []),
      ...(isBusinessFormActive
        ? [
            <HeadlineH7 mt={['sp_16', null, 'sp_20']} mb="sp_20" key="company-details-heading">
              {t('h24_company_details_heading')}
            </HeadlineH7>,
            {
              Component: StatefulInput,
              ComponentProps: {
                trimValue: true,
              },
              initialValue: '',
              name: FieldName.Company,
              label: t('h24_company_name'),
              validation: {
                required: true,
                rules: validationRules?.companyName,
              },
            },
            {
              Component: InputWithReplace,
              ComponentProps: {
                replace: removeWhiteSpaces,
              },
              initialValue: '',
              name: FieldName.VatNumber,
              label: t('h24_vat_number'),
              helpText: t('h24_vat_number_info'),
              validation: {
                required: true,
                rules: validationRules?.vatTaxNumber,
                rulesErrorMessage: t('h24_validation_error_form_vatNumber_pattern'),
              },
            },
            <HeadlineH7 mt={['sp_16', null, 'sp_20']} mb="sp_20" key="contact-person-heading">
              {t('h24_contact_person_heading')}
            </HeadlineH7>,
          ]
        : []),
      {
        Component: adaptField(TitleField),
        initialValue: '',
        name: FieldName.Title,
        validation: {
          required: false,
          rules: null,
        },
      },
      {
        Component: StatefulInput,
        ComponentProps: {
          trimValue: true,
        },
        name: FieldName.FirstName,
        label: t('h24_first_name'),
        initialValue: initialFormData?.get(FieldName.FirstName) ?? '',
        validation: {
          required: true,
          rules: validationRules?.firstname,
        },
      },
      {
        Component: StatefulInput,
        ComponentProps: {
          trimValue: true,
        },
        label: t('h24_last_name'),
        name: FieldName.LastName,
        initialValue: initialFormData?.get(FieldName.LastName) ?? '',
        validation: {
          required: true,
          rules: validationRules?.lastname,
        },
      },
      {
        Component: InputWithReplace,
        ComponentProps: {
          type: 'email',
          replace: (value: string) => value.toLowerCase(),
        },
        name: FieldName.EmailAddress,
        label: t('h24_registration_form_email_address'),
        initialValue: initialFormData?.get(FieldName.EmailAddress) ?? '',
        validation: {
          required: true,
          rules: validationRules?.email,
        },
      },
      ...(!isThirdPartySignUpFlow
        ? [
            {
              name: FieldName.Password,
              Component: adaptFieldWithBlur(PasswordField),
              ComponentProps: {
                autoComplete: HTMLAutoCompleteAttribute.NewPassword,
                helpMessagesConfig: validationRules?.passwordHelpMessagesConfig,
              },
              label: t('h24_login_form_password'),
              validation: {
                required: true,
                rules: validationRules?.password,
              },
              initialValue: '',
            },
          ]
        : []),
      hasHomeClubNudge ? null : <Separator key="separator" mb="sp_16" />,
      ...(showHomeClubConsent
        ? [
            hasHomeClubNudge
              ? {
                  Component: adaptField(HomeClubConsentCheckbox),
                  name: FieldName.HomeClubSubscription,
                  ComponentProps: {
                    eventName: 'registration-form-hc-consent_click',
                  },
                  validation: {
                    required: false,
                    rules: null,
                  },
                  initialValue: '',
                }
              : {
                  Component: adaptField(ConsentCheckbox),
                  ComponentProps: {
                    labelText: 'h24_hc_registration_page_checkbox_disclaimer',
                    helpText: 'h24_hc_registration_page_checkbox_disclaimer_link',
                    eventName: 'registration-form-hc-consent_click',
                    onClick: onHomeClubLinkClick,
                  },
                  name: FieldName.HomeClubSubscription,
                  validation: {
                    required: false,
                    rules: null,
                  },
                  initialValue: '',
                },
          ]
        : []),
      hasHomeClubNudge
        ? {
            Component: adaptField(NewsletterConsentCheckbox),
            name: FieldName.NewsletterSubscription,
            validation: {
              required: false,
              rules: null,
            },
            initialValue: '',
          }
        : {
            Component: adaptField(ConsentCheckbox),
            ComponentProps: {
              labelText: 'h24_newsletter_disclaimer_label',
              helpText: 'h24_newsletter_disclaimer_link',
              helpTextUrl: 'h24_newsletter_disclaimer_link_url',
            },
            name: FieldName.NewsletterSubscription,
            validation: {
              required: false,
              rules: null,
            },
            initialValue: '',
          },
    ],
    [
      validationRules,
      onHomeClubLinkClick,
      showHomeClubConsent,
      isThirdPartySignUpFlow,
      isBusinessFormActive,
      hasHomeClubNudge,
    ]
  );

  return formGroups;
};
